import React from "react";
import PropTypes from "prop-types";
import { Button, Select, Tabs, Tag, TextInput, Trash01 } from "@opendate-app/setlist";
import TimeBasedPricing from "./TimeBasedPricing";
import { PUBLISHED_LOCK_NOTE_ID } from "./PublishedLock";
import {
  APPLIES_TO_OPTIONS,
  MONTHLY_NTH_OPTIONS,
  TIME_MODE_OPTIONS,
  WEEK_ORDER,
  dayLong,
  dayShort,
  formatEntryTime,
  ticketSetNameProblem,
  MAX_TICKET_SET_NAME_LENGTH,
} from "../ticketSets";

const toggled = (values, value) =>
  values.includes(value) ? values.filter((each) => each !== value) : [...values, value].sort();

const shortDate = (isoDate) =>
  new Date(`${isoDate}T12:00:00`).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });

const TicketSetSettings = ({
  set,
  sets,
  deletable,
  matchedDates,
  scheduledDates,
  scheduledWeekdays,
  times,
  seriesAge,
  ageOptions,
  samplePaid,
  onChange,
  onRename,
  onDelete,
  locked,
}) => {
  const [draft, setDraft] = React.useState(set.name);
  React.useEffect(() => setDraft(set.name), [set.key, set.name]);
  const nameError = ticketSetNameProblem(draft, sets, set.key);
  const commitName = () => {
    if (nameError || draft.trim() === set.name) return;
    onRename(draft.trim());
  };
  const someTimes = set.timeMode === "some";
  const pricingTimes = someTimes ? [...(set.times || [])].sort() : times;

  return (
    <section className="series-step-panel series-set-settings" data-testid="ticket-set-settings">
      <header className="series-step-panel-header">
        <h3 className="series-step-panel-title">Ticket Set Settings</h3>
        <span className="series-step-panel-hint">
          {matchedDates} of {scheduledDates} scheduled date{scheduledDates === 1 ? "" : "s"} use this set
        </span>
        {deletable && (
          <Button variant="link-gray" size="sm" icon={Trash01} className="series-row-action-destructive" onClick={onDelete}>
            Delete Set
          </Button>
        )}
      </header>

      <fieldset
        className="series-step-panel-body series-published-lock-body"
        disabled={Boolean(locked)}
        aria-describedby={locked ? PUBLISHED_LOCK_NOTE_ID : undefined}
      >
        <div className="series-set-fields">
          <TextInput
            id="ticket-set-name"
            label="Set Name"
            maxLength={MAX_TICKET_SET_NAME_LENGTH}
            value={draft}
            error={nameError || undefined}
            onChange={(event) => setDraft(event.target.value)}
            onBlur={commitName}
            onKeyDown={(event) => event.key === "Enter" && commitName()}
          />
          <Select
            id="ticket-set-age"
            label="Age Restriction"
            options={[
              { value: "", label: `Same as series (${seriesAge || "All Ages"})` },
              ...ageOptions.map((age) => ({ value: age, label: age })),
              // A published set keeps an age the team stopped offering.
              ...(set.ageRestriction && !ageOptions.includes(set.ageRestriction)
                ? [{ value: set.ageRestriction, label: `${set.ageRestriction} (no longer offered)` }]
                : []),
            ]}
            value={set.ageRestriction || ""}
            hint={set.ageRestriction ? "Overrides the series age restriction for this set" : undefined}
            disabled={Boolean(locked)}
            onChange={(age) => onChange({ ageRestriction: age || "" })}
          />
        </div>

        <span className="series-edit-label">Applies To</span>
        <Tabs
          variant="button-border"
          size="sm"
          collapseBelow={0}
          aria-label="Applies to"
          items={APPLIES_TO_OPTIONS}
          value={set.appliesTo}
          onChange={(appliesTo) => onChange({ appliesTo })}
        />

        {set.appliesTo === "all" && <p className="series-edit-hint">Covers every scheduled date that another set doesn&apos;t claim.</p>}

        {set.appliesTo === "days" && (
          <>
            <div className="series-weekday-row series-set-row">
              {WEEK_ORDER.map((day) => {
                const active = (set.days || []).includes(day);
                const off = !active && !scheduledWeekdays.includes(day);
                return (
                  <button
                    key={day}
                    type="button"
                    aria-pressed={active}
                    disabled={off}
                    title={off ? "Not in your schedule" : undefined}
                    className={["series-weekday", active ? "selected" : ""].join(" ").trim()}
                    onClick={() => onChange({ days: toggled(set.days || [], day) })}
                  >
                    {dayShort(day)}
                  </button>
                );
              })}
            </div>
            <p className="series-edit-hint">Only days in your schedule can be selected.</p>
          </>
        )}

        {set.appliesTo === "monthly" && (
          <div className="series-set-monthly series-set-row">
            <span>The</span>
            <Select
              id="ticket-set-monthly-nth"
              aria-label="Which week of the month"
              size="sm"
              options={MONTHLY_NTH_OPTIONS}
              value={Number(set.monthlyNth)}
              disabled={Boolean(locked)}
              onChange={(monthlyNth) => monthlyNth != null && onChange({ monthlyNth })}
            />
            <Select
              id="ticket-set-monthly-dow"
              aria-label="Which day of the week"
              size="sm"
              options={WEEK_ORDER.map((day) => ({ value: day, label: dayLong(day) }))}
              value={Number(set.monthlyDow)}
              disabled={Boolean(locked)}
              onChange={(monthlyDow) => monthlyDow != null && onChange({ monthlyDow })}
            />
            <span>of each month</span>
          </div>
        )}

        {set.appliesTo === "dates" && (
          <>
            <p className="series-edit-hint">Click dates in the calendar below to add or remove them.</p>
            {(set.dates || []).length > 0 && (
              <div className="series-set-row series-set-dates">
                {[...set.dates].sort().map((date) => (
                  <Tag key={date} size="sm" onDismiss={() => onChange({ dates: set.dates.filter((each) => each !== date) })}>
                    {shortDate(date)}
                  </Tag>
                ))}
              </div>
            )}
          </>
        )}

        <span className="series-edit-label">Entry Times</span>
        <Tabs
          variant="button-border"
          size="sm"
          collapseBelow={0}
          aria-label="Entry times"
          items={TIME_MODE_OPTIONS}
          value={someTimes ? "some" : "all"}
          onChange={(timeMode) => onChange({ timeMode })}
        />
        {someTimes &&
          (times.length > 0 ? (
            <div className="series-weekday-row series-set-row">
              {times.map((time) => {
                const active = (set.times || []).includes(time);
                return (
                  <button
                    key={time}
                    type="button"
                    aria-pressed={active}
                    className={["series-weekday", active ? "selected" : ""].join(" ").trim()}
                    onClick={() => onChange({ times: toggled(set.times || [], time) })}
                  >
                    {formatEntryTime(time)}
                  </button>
                );
              })}
            </div>
          ) : (
            <p className="series-edit-hint">Generate a schedule to choose entry times.</p>
          ))}
        <p className="series-edit-hint">Good for things like a 21+ after-hours entry or a members-only first hour.</p>

        <TimeBasedPricing
          adjustments={set.priceAdjustments || []}
          times={pricingTimes}
          samplePaid={samplePaid}
          locked={Boolean(locked)}
          onChange={(priceAdjustments) => onChange({ priceAdjustments })}
        />
      </fieldset>
    </section>
  );
};

TicketSetSettings.propTypes = {
  set: PropTypes.object.isRequired,
  sets: PropTypes.arrayOf(PropTypes.object).isRequired,
  deletable: PropTypes.bool,
  matchedDates: PropTypes.number.isRequired,
  scheduledDates: PropTypes.number.isRequired,
  scheduledWeekdays: PropTypes.arrayOf(PropTypes.number).isRequired,
  times: PropTypes.arrayOf(PropTypes.string).isRequired,
  seriesAge: PropTypes.string,
  ageOptions: PropTypes.arrayOf(PropTypes.string).isRequired,
  samplePaid: PropTypes.object,
  onChange: PropTypes.func.isRequired,
  onRename: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  locked: PropTypes.bool,
};

export default TicketSetSettings;
